import { useEffect, useState, useMemo, useCallback, memo } from "react";
import Post from "../post/Post";
import { useSelector, useDispatch } from "react-redux";
import { getPublicPostsAction } from "../../redux/actions/postActions";
import CommonLoading from "../loader/CommonLoading";
import LoadMoreButton from "../shared/LoadMoreButton";

const MemoizedPost = memo(Post);

const PublicPost = ({ publicUserId, userName }) => {
  const [loading, setLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);
  const dispatch = useDispatch();
  const publicPosts = useSelector((state) => state.posts?.publicPosts);
  const totalPublicPosts = useSelector((state) => state.posts?.totalPublicPosts);

  const LIMIT = 10;

  useEffect(() => {
    const getPublicPosts = async () => {
      setLoading(true);
      await dispatch(getPublicPostsAction(publicUserId, LIMIT, 0));
      setLoading(false);
    };
    getPublicPosts();
  }, [dispatch, publicUserId, LIMIT]);

  const handleLoadMore = useCallback(() => {
    setIsLoadMoreLoading(true);
    const currentLength = publicPosts?.length || 0;
    dispatch(getPublicPostsAction(publicUserId, LIMIT, currentLength)).finally(() => {
      setIsLoadMoreLoading(false);
    });
  }, [dispatch, publicUserId, LIMIT, publicPosts?.length]);

  // Memoize posts to prevent unnecessary re-renders
  const memoizedPosts = useMemo(() => {
    return publicPosts?.map((post) => (
      <MemoizedPost key={post._id} post={post} isLarge={true} />
    )) || [];
  }, [publicPosts]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <CommonLoading />
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-0">
      {publicPosts?.length === 0 ? (
        <div className="w-full bg-white border rounded-lg p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No posts yet</h3>
          <p className="text-gray-500 font-roboto-flex">
            {userName} hasn't shared anything yet. Check back later!
          </p>
        </div>
      ) : (
        <>
          <div className="w-full space-y-3 flex flex-col">
            {memoizedPosts}
          </div>
          
          {/* Load More Button */}
          {publicPosts?.length >= LIMIT && publicPosts?.length < totalPublicPosts && (
            <div className="mt-6">
              <LoadMoreButton
                onClick={handleLoadMore}
                isLoading={isLoadMoreLoading}
                text="Load More Posts"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PublicPost;
